import type { NavigationRaw } from "../index.js";
import type { NavigationInput } from "../lib/navigation.types.js";
import type { NavigationParameters, ParameterRecord, ParameterValue } from "./params.types.js";
import type { ObjectToEntries, ObjectToEntry } from "../lib/utils.js";
import type { NexusHandler } from "../../nexus/function/function.types.js";

export const locationToParams = (
  loc: NavigationInput,
  type: "search" | "hash",
): [location: URL, params: URLSearchParams] => {
  let url: URL;

  /*                       */
  if (typeof loc === "string")
    if (loc.startsWith("http")) url = new URL(loc);
    else {
      url = new URL(location.href);
      url[type] = loc;
    }
  else url = new URL(loc.href);

  /*                                                  */
  return [url, type === "hash" ? new URLSearchParams(url.hash.slice(1)) : url.searchParams];
};

const coerceValue = (value: string): unknown => {
  if (value === "undefined") return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const uncoerceValue = (value: unknown): string =>
  typeof value === "string" ? value : JSON.stringify(value);

/**
 *
 *
 *
 *
 *
 */
const reduceToRecord = <C extends [PropertyKey, ParameterValue]>(
  acc: Record<PropertyKey, ParameterValue>,
  [key, value]: C,
): Record<PropertyKey, ParameterValue> => {
  if (!(key in acc)) {
    acc[key] = value;
  } else if (Array.isArray(acc[key])) {
    acc[key].push(value as string);
  } else {
    acc[key] = [acc[key] as string, value as string];
  }

  return acc;
};

/**
 *
 *
 *
 *
 *
 */
const reduceToObject = <C extends [PropertyKey, ParameterValue]>(
  acc: Record<PropertyKey, unknown>,
  [key, value]: C,
): Record<PropertyKey, unknown> => {
  const coerced = coerceValue(value as string);

  if (!(key in acc)) {
    acc[key] = coerced;
  } else if (Array.isArray(acc[key])) {
    acc[key].push(coerced);
  } else {
    acc[key] = [acc[key] as string, coerced];
  }

  return acc;
};

const isValidKey = (key: string, filter: string[]): boolean => filter.includes(key);

function createParams<P extends ParameterRecord>(
  raw: URLSearchParams,
  type: "search" | "hash",
  url: URL,
  filter: string[] = [],
): NavigationParameters<P> {
  const res: NavigationParameters<P> = {
    *[Symbol.iterator]() {
      if (filter.length === 0)
        for (const [key, value] of raw) yield [key, value] as ObjectToEntry<P>;
      else
        for (const [key, value] of raw) {
          if (isValidKey(key, filter)) yield [key, value] as ObjectToEntry<P>;
        }
    },

    get [Symbol.toStringTag]() {
      return `${type[0].toUpperCase()}${type.slice(1)}Parameters`;
    },

    type,
    keys: raw.keys.bind(raw) as never,
    values: raw.values.bind(raw) as never,
    append: raw.append.bind(raw) as never,
    set: raw.set.bind(raw) as never,
    delete: raw.delete.bind(raw) as never,
    get: raw.get.bind(raw) as never,
    getAll: raw.getAll.bind(raw) as never,
    has: raw.has.bind(raw) as never,
    entries: raw.entries.bind(raw) as never,

    clear: (ignoreFilter) => {
      /*                                         */
      if (ignoreFilter || filter.length === 0) for (const key of raw.keys()) raw.delete(key);
      else for (const key of raw.keys()) if (isValidKey(key, filter)) raw.delete(key);
      return res;
    },

    toEntries: (current) => {
      /*                                           */
      const result = [...res] as string[][];
      if (!current) return result as never;

      /*                                */
      const curr = locationToParams(current, type)[1];

      /*                                                                        */
      result.forEach(([key]) => curr.delete(key as string));

      /*                   */
      return [...new URLSearchParams([...curr, ...result])] as never;
    },
    toRecord: (current) => res.toEntries(current).reduce(reduceToRecord, {}) as never,

    toObject: (current) => res.toEntries(current).reduce(reduceToObject, {}) as never,

    toString: (current) => res.toUrl(current)[type],

    toUrl: (current) => {
      if (!current) {
        const resUrl = new URL(url);
        resUrl[type] = decodeURI(raw.toString());
        return resUrl;
      }

      const [resUrl, curParams] = locationToParams(current, type);
      const filtered = [...res] as string[][];

      /*                                                                        */
      filtered.forEach(([key]) => curParams.delete(key as string));

      /*                   */
      resUrl[type] = decodeURI(new URLSearchParams([...curParams, ...filtered]).toString());
      return resUrl;
    },

    fromEntries: (entries) => {
      res.clear();

      for (const [key, value] of entries) {
        if (Array.isArray(value)) value.forEach((v) => raw.append(key as string, uncoerceValue(v)));
        else if (raw.has(key as string)) raw.append(key as string, uncoerceValue(value));
        else raw.set(key as string, uncoerceValue(value));
      }

      return res;
    },
    fromObject: (entries) => res.fromEntries(Object.entries(entries) as ObjectToEntries<unknown>),
    fromParams: (params) => res.fromEntries([...params] as ObjectToEntries<unknown>),
    fromString: (params) => res.fromParams(locationToParams(params, type)[1]),

    coerce: (key) => (res.has(key) ? (coerceValue(res.get(key)!) as never) : null),
    coerceAll: (key) => {
      const values = res.getAll(key);
      return values ? values.map((v) => coerceValue(v) as never) : /*                */ [];
    },

    appendMany: (...entries) => entries.forEach(([key, value]) => raw.append(key as string, value)),

    parse: (schema, params) => schema.parse(res.toRecord(), params),
    parseSafe: (schema, params) => schema.safeParse(res.toRecord(), params),

    clone: () => createParams(new URLSearchParams(raw), type, url, filter),
  };

  return res;
}

/**
 *
 */
export const hashParams = ((location, filter) => {
  const [url, raw] = locationToParams(location, "hash");
  return createParams(raw, "hash", url, filter);
}) satisfies NexusHandler<NavigationRaw["hashParams"]>;

/**
 *
 */
export const searchParams = ((location, filter) => {
  const [url, raw] = locationToParams(location, "search");
  return createParams(raw, "search", url, filter);
}) satisfies NexusHandler<NavigationRaw["searchParams"]>;
